import React, { useCallback, useEffect, useState } from 'react';
import type { TripResponseArgs } from '@this/domain/trip/trip2';
import { MESSAGE_TYPES } from '../../types/messages';
import type { ChatMessage, FeedbackType } from '../../types/messages';
import type { UserInfo } from '../../types/user';
import type { ProposalBundle } from '../../api/proposalBundles';
import type { ReservationInfo } from '../../api/reservationInfo';
import AiAvatar from '../icons/AiAvatar';
import UserAvatar from '../icons/UserAvatar';
import OptimisticMessage from './OptimisticMessage';
import ProposalSummaryMessage from './ProposalSummaryMessage';
import ReservationInfoForm from './ReservationInfoForm';
import type { FormValues } from './ReservationInfoFormTypes';
import ArrangementConfirmationMessage from './ArrangementConfirmationMessage';
import ArrangementCompletedMessage from './ArrangementCompletedMessage';
import SearchSupportInsightMessage from './SearchSupportInsightMessage';
import UnsupportedRequestMessage from './UnsupportedRequestMessage';
import CalendarConnectionPromptMessage from './CalendarConnectionPromptMessage';
import CalendarContextSummaryMessage from './CalendarContextSummaryMessage';
import { ProposalSection } from '../proposal';
import { filterMessagesForDisplay } from '../../utils/filterMessagesForDisplay';
import { formatTimestamp } from '../../utils/formatTimestamp';
import AiMessageFeedback from './AiMessageFeedback';

const FEEDBACK_ENABLED_TYPES = [MESSAGE_TYPES.MESSAGE] as const;

const shouldShowFeedback = (message: ChatMessage): boolean => {
  if (!message.type) return true;
  return (FEEDBACK_ENABLED_TYPES as readonly string[]).includes(message.type);
};

const FADE_IN_KEYFRAMES = `
@keyframes messageFadeIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
`;

const usePrefersReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return prefersReducedMotion;
};

const StyleInjector = () => {
  useEffect(() => {
    const styleId = 'message-fade-in-animation';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = FADE_IN_KEYFRAMES;
      document.head.appendChild(style);
    }
  }, []);
  return null;
};

const AnimatedMessageRow = ({
  children,
  isNew,
  isUser = false
}: {
  children: React.ReactNode;
  isNew?: boolean;
  isUser?: boolean;
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const shouldAnimate = isNew === true && !prefersReducedMotion;
  const [animationComplete, setAnimationComplete] = useState(false);

  const handleAnimationEnd = () => {
    setAnimationComplete(true);
  };

  const baseStyle = isUser ? messageStyles.messageRowUser : messageStyles.messageRow;
  const animationStyle: React.CSSProperties =
    shouldAnimate && !animationComplete
      ? {
          animation: 'messageFadeIn 0.4s ease-out forwards'
        }
      : {};

  return (
    <div style={{ ...baseStyle, ...animationStyle }} onAnimationEnd={handleAnimationEnd}>
      {children}
    </div>
  );
};

interface Props {
  trip: TripResponseArgs;
  user?: UserInfo;
  messages: ChatMessage[];
  proposalBundles?: ProposalBundle[];
  reservationInfo?: ReservationInfo | null;
  onSelectItem?: (bundleId: number, itemId: number) => void;
  onCloseItem?: (bundleId: number) => void;
  onSubmitReservationInfo?: (values: FormValues) => void;
  isSubmittingReservationInfo?: boolean;
  onArrangementRequest?: () => void;
  isRequestingArrangement?: boolean;
  isArrangementRequested?: boolean;
  proposalChangeDisabled?: boolean;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  onCalendarDateSelect?: (dateText: string) => void;
}

const getUserInitials = (user?: UserInfo): string => {
  if (!user) return 'YOU';
  if (user.name) {
    const name = user.name.trim();
    if (name.length > 0) {
      return name.slice(0, 1);
    }
  }
  return 'YOU';
};

const MessageSection = ({
  trip,
  user,
  messages,
  proposalBundles = [],
  reservationInfo,
  onSelectItem,
  onCloseItem,
  onSubmitReservationInfo,
  isSubmittingReservationInfo = false,
  onArrangementRequest,
  isRequestingArrangement = false,
  isArrangementRequested = false,
  proposalChangeDisabled = false,
  scrollContainerRef,
  onCalendarDateSelect
}: Props) => {
  const styles = messageStyles;
  const userInitials = getUserInitials(user);
  const displayMessages = filterMessagesForDisplay(messages);
  const [feedbackOverrides, setFeedbackOverrides] = useState<Record<string, FeedbackType>>({});

  const handleFeedbackUpdate = useCallback((messageId: string, feedback: FeedbackType) => {
    setFeedbackOverrides(prev => ({ ...prev, [messageId]: feedback }));
  }, []);

  // TODO: OrganizationResponseArgs に include_tax を追加後、trip?.user?.organization?.include_tax から取得する
  const includeTax = false;

  const renderReservationInfoFormMessage = (message: ChatMessage) => {
    if (!reservationInfo || !onSubmitReservationInfo) return null;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <div style={styles.aiBubble}>{message.text}</div>
          <div style={{ marginTop: '12px' }}>
            <ReservationInfoForm
              reservationInfo={reservationInfo}
              onSubmit={onSubmitReservationInfo}
              isSubmitting={isSubmittingReservationInfo}
              editable={message.editable !== false}
            />
          </div>
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderProposalMessage = (message: ChatMessage) => {
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <ProposalSection
            bundles={proposalBundles}
            includeTax={includeTax}
            introMessage={message.text}
            onSelect={onSelectItem}
            onClose={onCloseItem}
            disabled={proposalChangeDisabled}
            tripId={trip.id}
          />
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderOptimisticMessage = (message: ChatMessage) => (
    <AnimatedMessageRow key={message.id} isNew={message.isNew}>
      <div style={styles.avatar}>
        <AiAvatar />
      </div>
      <div style={styles.messageContentAi}>
        <OptimisticMessage text={message.text} />
      </div>
    </AnimatedMessageRow>
  );

  const renderSummaryMessage = (message: ChatMessage) => {
    if (!message.summary) return null;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <div style={styles.aiBubble}>
            <ProposalSummaryMessage text={message.text} summary={message.summary} includeTax={includeTax} />
          </div>
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderArrangementConfirmationMessage = (message: ChatMessage) => {
    if (!message.summary || !onArrangementRequest) return null;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <ArrangementConfirmationMessage
            text={message.text}
            summary={message.summary}
            includeTax={includeTax}
            onArrangementRequest={onArrangementRequest}
            isRequesting={isRequestingArrangement}
            isRequested={isArrangementRequested}
          />
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderArrangementCompletedMessage = (message: ChatMessage) => {
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <ArrangementCompletedMessage
            text={message.text}
            approvalRequired={message.approvalRequired ?? false}
            tripId={trip.id}
            isNewlyCompleted={message.isNewlyCompleted ?? false}
          />
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderSearchSupportInsightMessage = (message: ChatMessage) => {
    if (!message.insight) return null;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <SearchSupportInsightMessage text={message.text} insight={message.insight} />
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderUnsupportedRequestMessage = (message: ChatMessage) => {
    if (!message.unsupportedRequest) return null;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <UnsupportedRequestMessage unsupportedRequest={message.unsupportedRequest} />
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderCalendarConnectionPromptMessage = (message: ChatMessage) => {
    if (!message.calendarConnectionPrompt) return null;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <CalendarConnectionPromptMessage prompt={message.calendarConnectionPrompt} />
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderCalendarContextSummaryMessage = (message: ChatMessage) => {
    if (!message.calendarContextSummary) return null;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <CalendarContextSummaryMessage
            text={message.text}
            summary={message.calendarContextSummary}
            onSelectDate={onCalendarDateSelect}
          />
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderAiMessage = (message: ChatMessage) => {
    if (message.type === MESSAGE_TYPES.PROPOSAL) {
      return renderProposalMessage(message);
    }

    if (message.type === MESSAGE_TYPES.SUMMARY) {
      return renderSummaryMessage(message);
    }

    if (message.type === MESSAGE_TYPES.OPTIMISTIC) {
      return renderOptimisticMessage(message);
    }

    if (message.type === MESSAGE_TYPES.RESERVATION_INFO_FORM) {
      return renderReservationInfoFormMessage(message);
    }

    if (message.type === MESSAGE_TYPES.ARRANGEMENT_CONFIRMATION) {
      return renderArrangementConfirmationMessage(message);
    }

    if (message.type === MESSAGE_TYPES.ARRANGEMENT_COMPLETED) {
      return renderArrangementCompletedMessage(message);
    }
    if (message.type === MESSAGE_TYPES.SEARCH_SUPPORT_INSIGHT) {
      return renderSearchSupportInsightMessage(message);
    }
    if (message.type === MESSAGE_TYPES.UNSUPPORTED_REQUEST) {
      return renderUnsupportedRequestMessage(message);
    }
    if (message.type === MESSAGE_TYPES.CALENDAR_CONNECTION_PROMPT) {
      return renderCalendarConnectionPromptMessage(message);
    }
    if (message.type === MESSAGE_TYPES.CALENDAR_CONTEXT_SUMMARY) {
      return renderCalendarContextSummaryMessage(message);
    }
    const currentFeedback = feedbackOverrides[message.id] ?? message.feedback;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <div style={styles.aiBubble}>{message.text}</div>
          {shouldShowFeedback(message) && (
            <AiMessageFeedback
              messageId={message.id}
              tripId={String(trip.id)}
              currentFeedback={currentFeedback}
              onFeedbackUpdate={handleFeedbackUpdate}
            />
          )}
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderUserMessage = (message: ChatMessage) => (
    <AnimatedMessageRow key={message.id} isNew={message.isNew} isUser>
      <div style={styles.messageContentUser}>
        <div style={styles.userBubble}>{message.text || trip?.final_destination || ''}</div>
        <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
      </div>
      <div style={styles.avatar}>
        <UserAvatar initials={userInitials} />
      </div>
    </AnimatedMessageRow>
  );

  return (
    <div style={styles.messages} ref={scrollContainerRef}>
      <StyleInjector />
      {displayMessages.map(message =>
        message.direction === 'ai' ? renderAiMessage(message) : renderUserMessage(message)
      )}
    </div>
  );
};

const messageStyles = {
  messages: {
    flex: 1,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    overflow: 'auto' as const,
    backgroundColor: '#ffffff'
  },
  messageRow: {
    display: 'flex',
    alignItems: 'flex-start' as const,
    gap: '12px',
    width: '100%'
  },
  messageRowUser: {
    display: 'flex',
    alignItems: 'flex-start' as const,
    justifyContent: 'flex-end' as const,
    gap: '12px',
    width: '100%'
  },
  avatar: {
    flex: '0 0 48px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  messageContentAi: {
    flex: 1,
    maxWidth: '70%'
  },
  messageContentUser: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-end' as const,
    maxWidth: '70%',
    flex: 1
  },
  aiBubble: {
    padding: '12px 16px',
    borderRadius: '8px',
    backgroundColor: '#f5f5f5',
    display: 'inline-block' as const
  },
  userBubble: {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #83b9e4',
    backgroundColor: '#e6f4fb',
    display: 'inline-block' as const
  },
  timestamp: {
    fontSize: '11px',
    color: '#7d7d7d',
    marginTop: '4px'
  }
};

export default MessageSection;
